import { type ReactNode, use, useCallback, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { type SubscriptionListRow } from '@sr/types/subscription';
import { getArticleDataBySubscriptionType } from '@sr/utils';
import { useAccessToken } from '#lib/hooks/useAccessToken';
import { useTransactionId } from '../../hooks/useTransactionId';
import { getApiUrl } from '../../utils/environment';
import { type ConfirmationOrder } from '../Checkout/Confirmation/types';
import {
  type SubscriptionDetails,
  SubscriptionsContext,
  type SubscriptionsContextValue,
} from './SubscriptionsContext';
import { fetchPartnerInfo, fetchUserById } from './utils';

type SubscriptionsProviderProps = {
  children: ReactNode;
};

export const SubscriptionsProvider = ({ children }: SubscriptionsProviderProps) => {
  const { getAccessTokenSilently } = useAccessToken();
  const transactionId = useTransactionId();
  const [called, setCalled] = useState(false);

  const [subscriptions, setSubscriptions] = useState<SubscriptionListRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  // holds the ongoing fetch promise to dedupe concurrent calls
  const ongoingPromise = useRef<Promise<SubscriptionListRow[]> | null>(null);
  const getSubscriptions = useCallback(async (): Promise<SubscriptionListRow[]> => {
    // return cached value if present
    if (subscriptions || called) return subscriptions ?? [];

    // return existing in-flight promise if any
    if (ongoingPromise.current) return ongoingPromise.current;

    setCalled(true);

    const fetchPromise = (async () => {
      setLoading(true);
      try {
        const url = `${getApiUrl()}/subscription/subscriptions/my`;
        const accessToken = await getAccessTokenSilently();
        const headers = new Headers();

        headers.set('Authorization', `Bearer ${accessToken}`);
        headers.set('X-Transaction-Id', transactionId);

        const res = await fetch(url, { headers });

        if (!res.ok) {
          const responseBody = await res
            .clone()
            .text()
            .catch(() => 'Unable to read response body');

          Sentry.captureException(
            new Error(`Failed to fetch subscriptions: ${res.status} ${responseBody}`),
            {
              extra: {
                url,
                status: res.status,
                statusText: res.statusText,
                method: 'GET',
                responseBody,
                headers: Object.fromEntries(headers.entries()),
              },
              tags: {
                fetch_error: true,
              },
            },
          );

          throw new Error(`Failed to fetch subscriptions: ${res.status} ${responseBody}`);
        }
        const data = (await res.json()) as SubscriptionListRow[];

        setSubscriptions(data);

        return data;
      } finally {
        setLoading(false);
        ongoingPromise.current = null;
      }
    })();

    ongoingPromise.current = fetchPromise;

    return fetchPromise;
  }, [subscriptions, getAccessTokenSilently, transactionId, called]);

  const getSubscriptionDetailsById = useCallback(
    async (subscriptionId: string): Promise<SubscriptionDetails> => {
      const accessToken = await getAccessTokenSilently();

      // Fetch order details
      const orderUrl = `${getApiUrl()}/subscription/orders/${subscriptionId}`;
      const orderHeaders = new Headers();

      orderHeaders.set('Authorization', `Bearer ${accessToken}`);
      orderHeaders.set('X-Transaction-Id', transactionId);

      const orderRes = await fetch(orderUrl, { headers: orderHeaders });

      if (!orderRes.ok) {
        const text = await orderRes.text().catch(() => '');

        Sentry.captureException(
          new Error(`Failed to fetch subscription order: ${orderRes.status} ${text}`),
          {
            extra: {
              url: orderUrl,
              fetchOptions: { headers: Object.fromEntries(orderHeaders.entries()) },
              status: orderRes.status,
              statusText: orderRes.statusText,
              errorText: text,
            },
            tags: {
              fetch_error: true,
            },
          },
        );

        throw new Error(`Failed to fetch subscription order: ${orderRes.status} ${text}`);
      }

      const orderData = (await orderRes.json()) as Omit<
        ConfirmationOrder,
        'vat' | 'total' | 'vatRate'
      >;

      // Get VAT rate from article data based on subscription type and market
      const articleData = getArticleDataBySubscriptionType(orderData.type, orderData.market);
      const vatRate = articleData.vat;
      const vat = orderData.price * vatRate;
      const total = orderData.price + vat;

      const order: ConfirmationOrder = {
        ...orderData,
        vatRate,
        vat,
        total,
      };

      // Fetch user, purchaser (if different), and invoice partner in parallel
      const [user, partners] = await Promise.all([
        fetchUserById(order.userId, accessToken, transactionId),
        fetchPartnerInfo([order.invoicePartnerId], accessToken, transactionId),
      ]);

      // Fetch purchaser if buyerId exists and is different from userId
      let purchaser = user;

      if (order.buyerId && order.buyerId !== order.userId) {
        purchaser = await fetchUserById(order.buyerId, accessToken, transactionId);
      }

      if (partners.length === 0) {
        throw new Error(`Failed to fetch invoice partner with ID: ${order.invoicePartnerId}`);
      }

      const invoicePartner = partners[0];

      return {
        order,
        user,
        purchaser,
        invoicePartner,
      };
    },
    [getAccessTokenSilently, transactionId],
  );

  const value = useMemo(
    () => ({
      subscriptions,
      loading,
      getSubscriptions,
      getSubscriptionDetailsById,
    }),
    [subscriptions, loading, getSubscriptions, getSubscriptionDetailsById],
  );

  return <SubscriptionsContext value={value}>{children}</SubscriptionsContext>;
};

export const useSubscriptions = (): SubscriptionsContextValue => {
  const ctx = use(SubscriptionsContext);

  if (!ctx) {
    throw new Error('useSubscriptions must be used within a SubscriptionsProvider');
  }

  return ctx;
};
